/// <reference types="vite/client" />
import {
  HeadContent,
  Link,
  Outlet,
  Scripts,
  createRootRoute,
  useLocation,
} from "@tanstack/react-router";
import * as React from "react";
import appCss from "~/styles/app.css?url";
import { getLocale } from "~/paraglide/runtime";
import * as m from "~/paraglide/messages";
import { ToastProvider } from "@alchema/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, themeInitScript } from "~/lib/theme";
import { initPosthog, getPosthogClient, trackEvent } from "~/lib/posthog-client";
import { initSentryClient } from "~/lib/sentry-client";
import { installAffonsoConsentListener } from "~/lib/affonso-consent";
import { CookieConsent } from "~/components/CookieConsent";
import { MotionPreferenceProvider } from "~/providers/motion-preference-provider";
import { DisplayPreferencesProvider } from "~/providers/display-preferences-provider";
import { NoiseOverlay } from "~/components/NoiseOverlay";
import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "~/lib/locale";
import { AppShell } from "~/components/layout/AppShell";
import { queryClient } from "~/lib/query-client";
import { isBlogPath } from "~/lib/blog-url";
import { asAnalyticsLocale, canonicalAnalyticsRoute, classifyCampaignSource } from "~/lib/analytics-events";
import { isPublicSeoLandingPath } from "~/lib/seo/public-landing-routes";

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, minimum-scale=1" },
      { title: "Alchema — AI Governance, Security & Job Applications for Europe" },
      { name: "description", content: "AI-governed job applications for Europe — transparent AI controls, EU data processing (see Privacy Policy), human review, Article 50 transparency." },
      { property: "og:title", content: "Alchema — AI Governance, Security & Job Applications for Europe" },
      { property: "og:description", content: "Alchema gives European job seekers AI-assisted resume tailoring with governance controls, EU data security, and human review before every submission." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://alchema.eu" },
      { property: "og:locale", content: "en_EU" },
      { property: "og:image", content: "https://alchema.eu/og-default.png" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Alchema — AI Governance, Security & Job Applications for Europe" },
      { name: "twitter:description", content: "AI-governed job applications for Europe — transparent controls, EU data security, human review before every submission." },
      { name: "twitter:image", content: "https://alchema.eu/og-default.png" },
    ],
    links: [
      { rel: "alternate", type: "application/rss+xml", title: "Alchema Blog", href: "/feed.xml" },
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "icon", href: "/favicon-32x32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/favicon-16x16.png", type: "image/png", sizes: "16x16" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "preload", href: "/fonts/Geist-Variable.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preload", href: "/fonts/CabinetGrotesk-700.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://unpkg.com" },
      { rel: "dns-prefetch", href: "https://unpkg.com" },
      { rel: "dns-prefetch", href: "https://api.allorigins.win" },
      // Spline 3D scene is only used on the landing page — preconnect only, no preload
      // (preloading a multi-MB scene on every page steals bandwidth from API calls)
      { rel: "dns-prefetch", href: "https://prod.spline.design" },
      { rel: "preload", href: "/fonts/InterDisplay-Variable.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
    ],
  }),
  component: RootLayout,
  shellComponent: RootDocument,
});

function CanonicalTag() {
  const location = useLocation();
  if (
    isBlogPath(location.pathname) ||
    isPublicSeoLandingPath(location.pathname)
  ) return null;
  const origin = "https://alchema.eu";
  // Strip locale prefix to get the canonical path
  const path = location.pathname.replace(/^\/(de|fr|es|it|nl|pl|fi|da|sv)(\/|$)/, "/");
  const canonicalHref = `${origin}${path === "/" ? "" : path}`;
  return <link rel="canonical" href={canonicalHref} />;
}

function HreflangTags() {
  const location = useLocation();
  if (
    isBlogPath(location.pathname) ||
    isPublicSeoLandingPath(location.pathname)
  ) return null;
  const origin = "https://alchema.eu";
  // Strip locale prefix to get the canonical path
  const path = location.pathname.replace(/^\/(de|fr|es|it|nl|pl|fi|da|sv)(\/|$)/, "/");
  return (
    <>
      {SUPPORTED_LOCALES.map((locale) => {
        const href = locale === DEFAULT_LOCALE
          ? `${origin}${path}`
          : `${origin}/${locale}${path === "/" ? "" : path}`;
        return <link key={locale} rel="alternate" hrefLang={locale} href={href} />;
      })}
      <link rel="alternate" hrefLang="x-default" href={`${origin}${path}`} />
    </>
  );
}

const homepageNoScriptRevealCss = `
  [data-homepage-reveal-root]
    :is([data-homepage-hero-heading], [data-homepage-hero-description], .section-heading) {
    opacity: 1 !important;
    filter: none !important;
    transform: none !important;
    animation: none !important;
  }
`;

const homepageRevealWatchdogScript = `(() => {
  const arm = () => {
    window.setTimeout(() => {
      const body = document.body;
      if (body?.getAttribute("data-hydrated") === "true") return;
      if (document.querySelector("[data-homepage-reveal-root]")) {
        document.documentElement.setAttribute(
          "data-homepage-reveal-fallback",
          "hydration-timeout",
        );
      }
    }, 3000);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", arm, { once: true });
  } else {
    arm();
  }
})();`;

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang={getLocale()} suppressHydrationWarning>
      <head>
        <HeadContent />
        <CanonicalTag />
        <HreflangTags />
        {/* Fonts: Geist + Cabinet Grotesk both self-hosted via @font-face in app.css */}
        <script dangerouslySetInnerHTML={{ __html: themeInitScript }} />
        <script dangerouslySetInnerHTML={{ __html: homepageRevealWatchdogScript }} />
        <noscript>
          <style>{homepageNoScriptRevealCss}</style>
        </noscript>
        {/* Affonso affiliate pixel is injected client-side only after analytics consent — see installAffonsoConsentListener (D-16). */}
      </head>
      <body className="min-h-screen antialiased" data-hydrated="false" style={{ backgroundColor: "var(--bg)", color: "var(--text)" }}>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:rounded-[10px] focus:px-4 focus:py-2 focus:text-sm focus:font-medium"
          style={{ background: "var(--surface)", color: "var(--text)", border: "1px solid var(--border)" }}
        >
          {m.skip_to_main_content()}
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootLayout() {
  const location = useLocation();

  // Hydration sentinel for Playwright E2E tests
  React.useEffect(() => {
    document.body.setAttribute('data-hydrated', 'true');
  }, []);

  React.useEffect(() => {
    initSentryClient();
    initPosthog();

    // Gate the Affonso affiliate pixel behind the analytics consent category (D-16).
    // Keys off the same cookie-consent state + COOKIE_CONSENT_CHANGED_EVENT as PostHog;
    // applies current consent immediately and returns a cleanup removed on unmount.
    const removeAffonsoConsentListener = installAffonsoConsentListener();

    // Sync PostHog opt-in/out state with stored cookie consent
    const consent = typeof window !== "undefined" ? localStorage.getItem("cookie-consent") : null;
    if (consent === "accepted") {
      getPosthogClient()?.opt_in_capturing();
    } else if (consent === "declined") {
      getPosthogClient()?.opt_out_capturing();
    }
    // If consent is null, PostHog stays opted-out by default (opt_out_capturing_by_default: true)

    // Track session_started once per browser session (only fires if opted-in)
    if (typeof window !== "undefined" && !sessionStorage.getItem("alchema_session_started")) {
      sessionStorage.setItem("alchema_session_started", "1");
      trackEvent("session_started", {
        has_referrer: Boolean(document.referrer),
        locale: asAnalyticsLocale(document.documentElement.lang),
      });
    }

    // Track campaign attribution from UTM params on first load
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("utm_source") || params.get("utm_campaign")) {
        const route = canonicalAnalyticsRoute(window.location.pathname);
        trackEvent("campaign_landed", {
          source: classifyCampaignSource(params.get("utm_source") ?? params.get("utm_medium")),
          landing_route: route === "home" || route === "pricing" || route === "blog" ? route : "other",
        });
      }
    }

    return removeAffonsoConsentListener;
  }, []);

  // Track page views on route changes
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    trackEvent("page_viewed", {
      route: canonicalAnalyticsRoute(location.pathname),
    });
  }, [location.pathname]);

  return (
    <ThemeProvider>
      <MotionPreferenceProvider>
        <DisplayPreferencesProvider>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            {location.pathname === "/" && <NoiseOverlay />}
            <AppShell />
            <CookieConsent />
          </ToastProvider>
        </QueryClientProvider>
        </DisplayPreferencesProvider>
      </MotionPreferenceProvider>
    </ThemeProvider>
  );
}
